import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import SettingsModules from './SettingsModules';
import { apiUrl } from './utils/api';

function RetellManagement({ initialSection = '' }) {
  const navigate = useNavigate();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [diagnosticSection, setDiagnosticSection] = useState('crm');
  const [diagnosticArea, setDiagnosticArea] = useState('crm');
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [diagnosticError, setDiagnosticError] = useState('');
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [crmDetailView, setCrmDetailView] = useState('prospects');
  const [activeSection, setActiveSection] = useState(initialSection);

  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);

  useEffect(() => {
    const token = localStorage.getItem('management_token');
    const expires = localStorage.getItem('management_expires');
    const expired = expires && Date.now() > parseInt(expires, 10);

    if (!token || expired) {
      localStorage.removeItem('management_token');
      localStorage.removeItem('management_expires');
      setSessionActive(false);
      setSessionChecked(true);
      navigate('/settings/management-setup', { replace: true });
      return;
    }

    setSessionActive(true);
    setSessionChecked(true);
  }, [navigate]);

  if (!sessionChecked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Management Hub"
          subtitle="Checking protected session"
          backTo="/admin"
          backLabel="Administration"
          sticky
        />
        <main className="max-w-6xl mx-auto px-6 py-8">
          <div className="bg-white rounded-lg border border-gray-200 shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900">Checking management session...</h2>
            <p className="text-sm text-gray-600 mt-2">Please wait while DoorBase opens the protected area.</p>
          </div>
        </main>
      </div>
    );
  }

  if (!sessionActive) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Management Hub"
          subtitle="Protected platform management area"
          backTo="/admin"
          backLabel="Administration"
          sticky
        />
        <main className="max-w-6xl mx-auto px-6 py-8">
          <div className="bg-white rounded-lg border border-gray-200 shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900">Management session required</h2>
            <p className="text-sm text-gray-600 mt-2">Unlock the Management Hub to continue.</p>
            <button
              onClick={() => navigate('/settings/management-setup')}
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              Unlock Management
            </button>
          </div>
        </main>
      </div>
    );
  }

  const handleLock = () => {
    localStorage.removeItem('management_token');
    localStorage.removeItem('management_expires');
    navigate('/admin');
  };

  const runDiagnostic = async () => {
    setDiagnosticLoading(true);
    setDiagnosticError('');
    setDiagnosticData(null);

    const token = localStorage.getItem('management_token');
    const appToken = localStorage.getItem('doorbase_token');
    const endpoints = {
      crm: '/api/management/crm-diagnostics',
      financials: '/api/management/financial-diagnostics',
      operationsQuotes: '/api/management/quote-diagnostics',
      prospectQuotes: '/api/management/quote-diagnostics'
    };

    try {
      const response = await fetch(apiUrl(endpoints[diagnosticArea]), {
        headers: {
          'x-management-token': token || '',
          ...(appToken ? { Authorization: `Bearer ${appToken}` } : {})
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Diagnostic check failed');
      }

      setDiagnosticData(data);
    } catch (err) {
      setDiagnosticError(err.message || 'Diagnostic check failed');
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const totalFromCounts = (rows = []) => rows.reduce((total, row) => total + Number(row.count || 0), 0);
  const diagnosticOptions = {
    crm: [
      { value: 'crm', label: 'Prospects & Suppliers' },
      { value: 'prospectQuotes', label: 'Prospect Quotes' }
    ],
    operations: [
      { value: 'operationsQuotes', label: 'Customer Quotes' }
    ],
    financials: [
      { value: 'financials', label: 'Invoices' }
    ]
  };

  const sectionTiles = [
    {
      key: 'diagnostics',
      title: 'Diagnostics',
      icon: '🔎',
      description: 'Run protected checks against data, routes, modules, and platform health.',
      path: '/settings/management-diagnostics'
    },
    {
      key: 'retell',
      title: 'Retell Admin',
      icon: '☎️',
      description: 'Manage Retell prompts, agent setup, GitHub history, and Retell sync.',
      path: '/settings/retell-management'
    },
    {
      key: 'modules',
      title: 'Additional Modules',
      icon: '🧩',
      description: 'Switch tenant SaaS modules on or off.',
      path: '/settings/additional-modules'
    }
  ];

  const pageTitle = activeSection === 'diagnostics'
    ? 'Diagnostics'
    : activeSection === 'modules'
      ? 'Additional Modules'
      : 'Management Hub';

  const pageSubtitle = activeSection === 'diagnostics'
    ? 'Protected platform checks'
    : activeSection === 'modules'
      ? 'Tenant SaaS module switches'
      : 'Protected platform management area';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        backTo={activeSection ? '/settings/management-hub' : '/admin'}
        backLabel={activeSection ? 'Management Hub' : 'Administration'}
        sticky
      />

      <main className="max-w-6xl mx-auto px-6 py-8">
        {!activeSection && (
          <div className="bg-gray-300 rounded-lg shadow p-6 mx-auto" style={{ width: 'fit-content', maxWidth: '1000px' }}>
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Quick Access</h2>
            <div className="grid grid-cols-3 gap-4 justify-items-center">
              {sectionTiles.map(tile => (
                <button
                  key={tile.key}
                  onClick={() => navigate(tile.path)}
                  className="flex flex-col items-center p-6 bg-white rounded-lg w-full max-w-xs border-2 border-black hover:border-blue-500 hover:shadow-lg transition-all group"
                >
                  <span className="text-5xl mb-3 group-hover:scale-110 transition-transform">{tile.icon}</span>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{tile.title}</h3>
                  <p className="text-xs text-gray-500 text-center">{tile.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="hidden bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔎</span>
            <div>
              <h2 className="font-semibold text-yellow-900">Management Hub route recovered</h2>
              <p className="text-sm text-yellow-800 mt-1">
                The protected password flow is working and this route is now live. The original management tools still need to be reconnected as part of the restoration audit.
              </p>
            </div>
          </div>
        </div>

        <div className="hidden bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-blue-900 mb-2">Known original behaviour</h2>
          <p className="text-sm text-blue-800">
            The Retell template area included an import workflow and a save button that uploaded the selected template to Retell through the DoorBase API layer and protected management endpoint.
          </p>
        </div>

        {activeSection === 'diagnostics' && (
        <section className="bg-white border border-gray-200 rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Diagnostics</h2>
              <p className="text-sm text-gray-600 mt-1">Run protected read-only checks against this site's backend and database.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <label className="text-sm font-medium text-gray-700">
                Section
                <select
                  value={diagnosticSection}
                  onChange={(event) => {
                    const nextSection = event.target.value;
                    setDiagnosticSection(nextSection);
                    setDiagnosticArea(diagnosticOptions[nextSection][0].value);
                    setDiagnosticData(null);
                    setDiagnosticError('');
                  }}
                  className="block mt-1 w-48 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="crm">CRM</option>
                  <option value="operations">Operations</option>
                  <option value="financials">Financials</option>
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">
                Check
                <select
                  value={diagnosticArea}
                  onChange={(event) => {
                    setDiagnosticArea(event.target.value);
                    setDiagnosticData(null);
                    setDiagnosticError('');
                  }}
                  className="block mt-1 w-56 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {diagnosticOptions[diagnosticSection].map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <button
                onClick={runDiagnostic}
                disabled={diagnosticLoading}
                className="self-end px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg font-medium"
              >
                {diagnosticLoading ? 'Checking...' : 'Run Check'}
              </button>
            </div>
          </div>

          {diagnosticError && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
              {diagnosticError}
            </div>
          )}

          {diagnosticData && diagnosticArea === 'crm' && (
            <div className="mt-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500 font-semibold">Prospects</div>
                  <div className="text-2xl font-bold text-gray-900">{totalFromCounts(diagnosticData.prospects_by_organization)}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500 font-semibold">Suppliers</div>
                  <div className="text-2xl font-bold text-gray-900">{totalFromCounts(diagnosticData.suppliers_by_organization)}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500 font-semibold">Blank Prospects</div>
                  <div className="text-2xl font-bold text-gray-900">{diagnosticData.blank_prospects}</div>
                </div>
              </div>

              <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Current App User Visibility</h3>
                {diagnosticData.app_user ? (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-blue-700 font-medium">Email</div>
                      <div className="text-blue-950">{diagnosticData.app_user.email || '-'}</div>
                    </div>
                    <div>
                      <div className="text-blue-700 font-medium">Organization ID</div>
                      <div className="text-blue-950">{diagnosticData.app_user.organization_id || '-'}</div>
                    </div>
                    <div>
                      <div className="text-blue-700 font-medium">Visible Prospects</div>
                      <div className="text-blue-950">{diagnosticData.visible_to_app_user?.prospects ?? '-'}</div>
                    </div>
                    <div>
                      <div className="text-blue-700 font-medium">Visible Suppliers</div>
                      <div className="text-blue-950">{diagnosticData.visible_to_app_user?.suppliers ?? '-'}</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-blue-800">No normal DoorBase login token was found in this browser session.</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Prospects by Organization</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2">Organization ID</th>
                          <th className="text-left px-3 py-2">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(diagnosticData.prospects_by_organization || []).map(row => (
                          <tr key={`prospects-${row.organization_id}`} className="border-t border-gray-100">
                            <td className="px-3 py-2">{row.organization_id}</td>
                            <td className="px-3 py-2">{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Suppliers by Organization</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2">Organization ID</th>
                          <th className="text-left px-3 py-2">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(diagnosticData.suppliers_by_organization || []).map(row => (
                          <tr key={`suppliers-${row.organization_id}`} className="border-t border-gray-100">
                            <td className="px-3 py-2">{row.organization_id}</td>
                            <td className="px-3 py-2">{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>

              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">CRM Detail Records</h3>
                  <p className="text-sm text-gray-600 mt-1">Choose which record type to inspect.</p>
                </div>
                <label className="text-sm font-medium text-gray-700">
                  Show
                  <select
                    value={crmDetailView}
                    onChange={(event) => setCrmDetailView(event.target.value)}
                    className="block mt-1 w-44 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="prospects">Prospects</option>
                    <option value="suppliers">Suppliers</option>
                  </select>
                </label>
              </div>

              {crmDetailView === 'prospects' && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Recent Prospects</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2">ID</th>
                          <th className="text-left px-3 py-2">Org</th>
                          <th className="text-left px-3 py-2">Name</th>
                          <th className="text-left px-3 py-2">Contact</th>
                          <th className="text-left px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(diagnosticData.recent_prospects || []).map(row => (
                          <tr key={`recent-prospect-${row.id}`} className="border-t border-gray-100">
                            <td className="px-3 py-2">{row.id}</td>
                            <td className="px-3 py-2">{row.organization_id}</td>
                            <td className="px-3 py-2">{row.prospect_name || '-'}</td>
                            <td className="px-3 py-2">{row.contact_name || row.email || row.phone || '-'}</td>
                            <td className="px-3 py-2">{row.status || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {crmDetailView === 'suppliers' && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Recent Suppliers</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2">ID</th>
                          <th className="text-left px-3 py-2">Org</th>
                          <th className="text-left px-3 py-2">Company</th>
                          <th className="text-left px-3 py-2">Code</th>
                          <th className="text-left px-3 py-2">Contact</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(diagnosticData.recent_suppliers || []).map(row => (
                          <tr key={`recent-supplier-${row.id}`} className="border-t border-gray-100">
                            <td className="px-3 py-2">{row.id}</td>
                            <td className="px-3 py-2">{row.organization_id}</td>
                            <td className="px-3 py-2">{row.company_name || '-'}</td>
                            <td className="px-3 py-2">{row.supplier_code || '-'}</td>
                            <td className="px-3 py-2">{row.contact_name || row.email || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </div>
          )}

          {diagnosticData && diagnosticArea === 'financials' && (
            <div className="mt-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500 font-semibold">Invoices</div>
                  <div className="text-2xl font-bold text-gray-900">{totalFromCounts(diagnosticData.invoices_by_organization)}</div>
                </div>
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <div className="text-xs uppercase text-blue-700 font-semibold">Visible To Current User</div>
                  <div className="text-2xl font-bold text-blue-950">{diagnosticData.visible_to_app_user?.invoices ?? '-'}</div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Invoices by Organization</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2">Organization ID</th>
                        <th className="text-left px-3 py-2">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagnosticData.invoices_by_organization || []).map(row => (
                        <tr key={`invoices-${row.organization_id}`} className="border-t border-gray-100">
                          <td className="px-3 py-2">{row.organization_id}</td>
                          <td className="px-3 py-2">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Recent Invoices</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2">ID</th>
                          <th className="text-left px-3 py-2">Org</th>
                          <th className="text-left px-3 py-2">Ref</th>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-left px-3 py-2">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(diagnosticData.recent_invoices || []).map(row => (
                          <tr key={`recent-invoice-${row.id}`} className="border-t border-gray-100">
                            <td className="px-3 py-2">{row.id}</td>
                            <td className="px-3 py-2">{row.organization_id}</td>
                            <td className="px-3 py-2">{row.invoice_ref || '-'}</td>
                            <td className="px-3 py-2">{row.invoice_date ? new Date(row.invoice_date).toLocaleDateString('en-GB') : '-'}</td>
                            <td className="px-3 py-2">{row.status || '-'}</td>
                            <td className="px-3 py-2">{row.total || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {diagnosticData && diagnosticArea === 'operationsQuotes' && (
            <div className="mt-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500 font-semibold">Customer Quotes</div>
                  <div className="text-2xl font-bold text-gray-900">{diagnosticData.quotes?.total ?? '-'}</div>
                </div>
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <div className="text-xs uppercase text-blue-700 font-semibold">Visible Customer Quotes</div>
                  <div className="text-2xl font-bold text-blue-950">{diagnosticData.visible_to_app_user?.quotes ?? '-'}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500 font-semibold">Missing Org ID</div>
                  <div className="text-2xl font-bold text-gray-900">{diagnosticData.quotes?.missing_organization_id ?? '-'}</div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Customer Quotes Schema</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500 font-medium">Table Exists</div>
                    <div className="text-gray-900">{diagnosticData.schema?.quotes?.exists ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">Has Org Column</div>
                    <div className="text-gray-900">{diagnosticData.schema?.quotes?.has_organization_id ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">Visible To User</div>
                    <div className="text-gray-900">{diagnosticData.visible_to_app_user?.quotes ?? '-'}</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Customer Quotes by Organization</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2">Organization ID</th>
                        <th className="text-left px-3 py-2">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagnosticData.quotes?.by_organization || []).map(row => (
                        <tr key={`quote-org-${row.organization_id}`} className="border-t border-gray-100">
                          <td className="px-3 py-2">{row.organization_id}</td>
                          <td className="px-3 py-2">{row.count}</td>
                        </tr>
                      ))}
                      {(diagnosticData.quotes?.by_organization || []).length === 0 && (
                        <tr>
                          <td className="px-3 py-2 text-gray-500" colSpan="2">No organization breakdown available.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Recent Customer Quotes</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2">ID</th>
                        <th className="text-left px-3 py-2">Org</th>
                        <th className="text-left px-3 py-2">Ref</th>
                        <th className="text-left px-3 py-2">Customer</th>
                        <th className="text-left px-3 py-2">Site</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagnosticData.quotes?.recent || []).map(row => (
                        <tr key={`recent-quote-${row.id}`} className="border-t border-gray-100">
                          <td className="px-3 py-2">{row.id}</td>
                          <td className="px-3 py-2">{row.organization_id ?? '-'}</td>
                          <td className="px-3 py-2">{row.quote_ref || '-'}</td>
                          <td className="px-3 py-2">{row.customer_name || row.customer_id || '-'}</td>
                          <td className="px-3 py-2">{row.site_name || row.site_id || '-'}</td>
                          <td className="px-3 py-2">{row.status || '-'}</td>
                          <td className="px-3 py-2">{row.total || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {diagnosticData && diagnosticArea === 'prospectQuotes' && (
            <div className="mt-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500 font-semibold">Prospect Quotes</div>
                  <div className="text-2xl font-bold text-gray-900">{diagnosticData.prospect_quotes?.total ?? '-'}</div>
                </div>
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <div className="text-xs uppercase text-blue-700 font-semibold">Visible Prospect Quotes</div>
                  <div className="text-2xl font-bold text-blue-950">{diagnosticData.visible_to_app_user?.prospect_quotes ?? '-'}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500 font-semibold">Missing Org ID</div>
                  <div className="text-2xl font-bold text-gray-900">{diagnosticData.prospect_quotes?.missing_organization_id ?? '-'}</div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Prospect Quotes Schema</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500 font-medium">Table Exists</div>
                    <div className="text-gray-900">{diagnosticData.schema?.prospect_quotes?.exists ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">Has Org Column</div>
                    <div className="text-gray-900">{diagnosticData.schema?.prospect_quotes?.has_organization_id ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">Visible To User</div>
                    <div className="text-gray-900">{diagnosticData.visible_to_app_user?.prospect_quotes ?? '-'}</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Prospect Quotes by Organization</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2">Organization ID</th>
                        <th className="text-left px-3 py-2">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagnosticData.prospect_quotes?.by_organization || []).map(row => (
                        <tr key={`prospect-quote-org-${row.organization_id}`} className="border-t border-gray-100">
                          <td className="px-3 py-2">{row.organization_id}</td>
                          <td className="px-3 py-2">{row.count}</td>
                        </tr>
                      ))}
                      {(diagnosticData.prospect_quotes?.by_organization || []).length === 0 && (
                        <tr>
                          <td className="px-3 py-2 text-gray-500" colSpan="2">No organization breakdown available.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Recent Prospect Quotes</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2">ID</th>
                        <th className="text-left px-3 py-2">Org</th>
                        <th className="text-left px-3 py-2">Ref</th>
                        <th className="text-left px-3 py-2">Prospect</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagnosticData.prospect_quotes?.recent || []).map(row => (
                        <tr key={`recent-prospect-quote-${row.id}`} className="border-t border-gray-100">
                          <td className="px-3 py-2">{row.id}</td>
                          <td className="px-3 py-2">{row.organization_id ?? '-'}</td>
                          <td className="px-3 py-2">{row.quote_ref || '-'}</td>
                          <td className="px-3 py-2">{row.prospect_name || row.prospect_id || '-'}</td>
                          <td className="px-3 py-2">{row.status || '-'}</td>
                          <td className="px-3 py-2">{row.total || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </section>
        )}

        {activeSection === 'modules' && (
          <SettingsModules />
        )}

        <div className="mt-8 flex gap-3">
          <button
            onClick={handleLock}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
          >
            Lock Management Hub
          </button>
        </div>
      </main>
    </div>
  );
}

export default RetellManagement;
